/**
 * Single error type for everything that can go wrong talking to the backend.
 * `code` is the stable machine code from the backend envelope (API.md §11) or a
 * client-side code below; UI text comes from i18n `errors.<code>`.
 */

/** Client-side codes, never sent by the backend. */
export const ClientErrorCode = {
  NETWORK_ERROR: 'NETWORK_ERROR',
  UNEXPECTED_RESPONSE: 'UNEXPECTED_RESPONSE',
} as const;

export type ValidationFields = Record<string, string[]>;

export interface ApiErrorEnvelope {
  error: {
    code: string;
    message: string;
    details: Record<string, unknown> | null;
    request_id: string | null;
  };
}

export class ApiError extends Error {
  override readonly name = 'ApiError';
  /** HTTP status; `0` when the request never got a response. */
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown> | null;
  readonly requestId: string | null;

  constructor(
    status: number,
    code: string,
    message: string,
    details: Record<string, unknown> | null = null,
    requestId: string | null = null,
  ) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
    this.requestId = requestId;
  }

  /** Field errors of a `VALIDATION_ERROR` response (API.md §12), or `{}`. */
  get fieldErrors(): ValidationFields {
    const fields = this.details?.fields;
    return typeof fields === 'object' && fields !== null ? (fields as ValidationFields) : {};
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export function isErrorEnvelope(body: unknown): body is ApiErrorEnvelope {
  if (typeof body !== 'object' || body === null || !('error' in body)) return false;
  const { error } = body;
  return typeof error === 'object' && error !== null && 'code' in error;
}
